<template>
    <div class="row nav-padding">
        <div class="d-flex justify-content-start px-3 pt-3 w-full">
            <div class="d-flex align-items-center mr-2 col-md-4">
                <div class="mr-2 col-md-10">
                    <el-input
                        placeholder="Search Account Name, ID or Carrier SID"
                        clearable
                        @keyup.enter.native="handleSearch"
                        v-model="search_text">
                        <i slot="prefix" class="el-input__icon el-icon-search"></i>
                    </el-input>
                </div>
                <div>
                    <el-button
                        @click="handleSearch"
                        type="success"
                        :disabled="is_fetching">
                        Search
                    </el-button>
                </div>
            </div>
            <div v-if="carriers.length" class="d-flex align-items-center mt-2 ml-4">
                <div>
                    <span class="_700">Results:</span> {{ carriers.length }}
                </div>
            </div>
        </div>
        <div class="w-100 pt-5">
            <div
                v-if="carriers.length"
                class="p-3">
                <el-table
                    :data="carriers"
                    style="width: 100%">
                    <el-table-column
                        prop="id"
                        width="50"
                        label="ID"
                    />
                    <el-table-column
                        width="100"
                        label="Account ID">
                        <template v-slot="scope">
                            <p class="mb-0">{{ scope.row.company_id }}</p>
                        </template>
                    </el-table-column>
                    <el-table-column
                        label="Account Name">
                        <template v-slot="scope">
                            <p class="mb-0">{{ scope.row.company_name }}</p>
                        </template>
                    </el-table-column>
                    <el-table-column
                        label="Carrier Name">
                        <template v-slot="scope">
                            <p class="mb-0">{{ scope.row.name }}</p>
                        </template>
                    </el-table-column>
                    <el-table-column
                        label="Use Case">
                        <template v-slot="scope">
                            <p class="mb-0">{{ scope.row.is_fax ? 'fax' : scope.row.use_case }}</p>
                        </template>
                    </el-table-column>
                    <el-table-column
                        label="SID">
                        <template v-slot="scope">
                            <p class="mb-0">{{ scope.row.is_fax ? scope.row.app_id : scope.row.sid }}</p>
                        </template>
                    </el-table-column>
                    <el-table-column
                        label="Status">
                        <template v-slot="scope">
                            <b :class="'label ' + getCarrierStatusClass(scope.row.status)">{{ getCarrierStatusText(scope.row.status) }}</b>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
            <div
                v-else
                class="h-100 d-flex align-items-center">
                <div v-if="searching" class="w-100">
                    <h3 class="text-center">Loading...</h3>
                </div>
                <div v-else-if="no_results" class="w-100">
                    <h3 class="text-center">No results.</h3>
                </div>
                <div v-else class="w-100 search-resource-text">
                    <div class="text-center">
                        <i class="fa fa-search"></i>
                        <p class="mb-0">Search carriers</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as CarrierStatus from '../../../user/constants/carriers'

export default {
    data() {
        return {
            carriers: [],
            search_text: '',
            searching: false,
            no_results: false,
            is_fetching: false
        }
    },
    watch: {
        search_text(new_value) {
            if (!new_value.trim()) {
                // let's clean the carriers results if the search text is empty
                this.carriers = []
                this.no_results = false
            }
        }
    },

    methods: {
        handleSearch() {
            if (!this.search_text) {
                this.$notify.error({
                    title: 'Error',
                    message: 'Please enter a company id, name, or carrier sid before searching'
                })
                this.carriers = []
                this.no_results = false
                return
            }
            const search_text = this.determineSearchType(this.search_text.trim())
            this.searching = true
            this.is_fetching = true

            axios.get(`/api/admin/carriers?search_text=${search_text}`)
                .then(response => {
                    this.carriers = response.data
                    this.no_results = this.carriers.length === 0
                })
                .catch(error => {
                    console.error(error)
                    this.$notify.error({
                        title: 'Error',
                        message: error.message
                    })
                })
                .finally(() => {
                    this.searching = false
                    this.is_fetching = false
                })
        },

        determineSearchType(search_text) {
            const is_numeric = !isNaN(search_text) && search_text.trim() !== ''
            const has_hyphens = search_text.includes('-')
            const parts = search_text.split('-')
            // for some cases like 1898931920071296255 that are real sid and considering that the company_id is not greater than 13 characters
            const is_long_number = is_numeric && search_text.length > 13
            const starts_with_ac = search_text.startsWith("AC")
            // Check if the text could be a UUID
            const could_be_UUID = has_hyphens && parts.length === 5 && parts.every(part => /^[0-9a-fA-F]+$/.test(part)) && parts.map(part => part.length).toString() === '8,4,4,4,12'

            let search_text_with_prefix = search_text
            if (starts_with_ac || is_long_number || could_be_UUID) {
                search_text_with_prefix = "sid:" + search_text
            } else if (is_numeric) {
                search_text_with_prefix = "company_id:" + search_text
            } else {
                search_text_with_prefix = "company_name:" + search_text
            }
            return search_text_with_prefix
        },

        getCarrierStatusClass(status) {
            const statusColorMap = {
                [CarrierStatus.STATUS_ACTIVE]: 'success',
                [CarrierStatus.STATUS_SUSPENDED]: 'secondary',
                [CarrierStatus.STATUS_CLOSED]: 'warning',
                [CarrierStatus.STATUS_PURGED]: 'danger'
            }
            return statusColorMap[status] || 'light-blue'
        },

        getCarrierStatusText(status) {
            const statusTextMap = {
                [CarrierStatus.STATUS_STAGED]: 'Not Synced',
                [CarrierStatus.STATUS_ACTIVE]: 'Active',
                [CarrierStatus.STATUS_SUSPENDED]: 'Suspended',
                [CarrierStatus.STATUS_CLOSED]: 'Closed',
                [CarrierStatus.STATUS_PURGED]: 'Purged'
            }
            return statusTextMap[status] || 'Unknown'
        }
    }
}
</script>
